export type NoticePartKind = 'text' | 'photos' | 'linkedPhotos';

export type NoticeTextFold = 'auto' | 'never';

export type NoticeTextPart = {
  id: string;
  kind: 'text';
  body: string;
  fold: NoticeTextFold;
};

export type NoticePhotoCard = {
  id: string;
  imageId: string;
};

export type NoticeLinkedPhotoCard = {
  id: string;
  imageId: string;
  href: string | null;
};

export type NoticePhotosPart = {
  id: string;
  kind: 'photos';
  cards: NoticePhotoCard[];
};

export type NoticeLinkedPhotosPart = {
  id: string;
  kind: 'linkedPhotos';
  cards: NoticeLinkedPhotoCard[];
};

export type NoticePart =
  | NoticeTextPart
  | NoticePhotosPart
  | NoticeLinkedPhotosPart;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function parseHref(value: unknown): string | null {
  const href = asTrimmedString(value);
  if (!href) return null;
  // RTE と同型: javascript: / data: 等を弾く（data-access は rich-text に依存しない）
  const colon = href.indexOf(':');
  if (colon !== -1) {
    const proto = href.slice(0, colon + 1).toLowerCase();
    if (
      proto === 'javascript:' ||
      proto === 'data:' ||
      proto === 'vbscript:' ||
      proto === 'file:'
    ) {
      return null;
    }
  }
  return href;
}

function parsePhotoCard(value: unknown): NoticePhotoCard | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const imageId = asTrimmedString(rec['imageId'] ?? rec['image_id']);
  return {
    id: asTrimmedString(rec['id']) || crypto.randomUUID(),
    imageId,
  };
}

function parseLinkedPhotoCard(value: unknown): NoticeLinkedPhotoCard | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const imageId = asTrimmedString(rec['imageId'] ?? rec['image_id']);
  return {
    id: asTrimmedString(rec['id']) || crypto.randomUUID(),
    imageId,
    href: parseHref(rec['href']),
  };
}

function cardsFromUnknown(
  value: unknown,
  linked: boolean,
): NoticePhotoCard[] | NoticeLinkedPhotoCard[] {
  if (!Array.isArray(value)) return [];
  if (linked) {
    return value
      .map(parseLinkedPhotoCard)
      .filter((card): card is NoticeLinkedPhotoCard => card != null);
  }
  return value
    .map(parsePhotoCard)
    .filter((card): card is NoticePhotoCard => card != null);
}

function parsePart(value: unknown): NoticePart | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const kind = rec['kind'];
  const id = asTrimmedString(rec['id']) || crypto.randomUUID();
  if (kind === 'text') {
    const fold = rec['fold'] === 'never' ? 'never' : 'auto';
    return {
      id,
      kind: 'text',
      body: typeof rec['body'] === 'string' ? rec['body'] : '',
      fold,
    };
  }
  if (kind === 'photos') {
    return {
      id,
      kind: 'photos',
      cards: cardsFromUnknown(rec['cards'], false) as NoticePhotoCard[],
    };
  }
  if (kind === 'linkedPhotos') {
    return {
      id,
      kind: 'linkedPhotos',
      cards: cardsFromUnknown(rec['cards'], true) as NoticeLinkedPhotoCard[],
    };
  }
  return null;
}

export function parseNoticeParts(raw: unknown): NoticePart[] {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value
    .map(parsePart)
    .filter((part): part is NoticePart => part != null);
}

export function noticePartIsVisible(part: NoticePart): boolean {
  if (part.kind === 'text') {
    return (
      part.body
        .replace(/<br\s*\/?>/gi, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, '')
        .trim().length > 0
    );
  }
  if (part.kind === 'photos') {
    return part.cards.some((card) => card.imageId.length > 0);
  }
  return part.cards.some(
    (card) => card.imageId.length > 0 && Boolean(card.href),
  );
}

export function noticeHasVisibleParts(parts: NoticePart[]): boolean {
  return parts.some(noticePartIsVisible);
}

export function serializeNoticeParts(parts: NoticePart[]): NoticePart[] {
  return parts
    .map((part) => {
      if (part.kind === 'text') return part;
      if (part.kind === 'photos') {
        return {
          ...part,
          cards: part.cards.filter((card) => card.imageId.length > 0),
        };
      }
      return {
        ...part,
        cards: part.cards.filter(
          (card) => card.imageId.length > 0 && Boolean(card.href),
        ),
      };
    })
    .filter((part) => {
      if (part.kind === 'text') return noticePartIsVisible(part);
      return part.cards.length > 0;
    });
}

export function createNoticeTextPart(
  body = '',
  fold: NoticeTextFold = 'auto',
): NoticeTextPart {
  return { id: crypto.randomUUID(), kind: 'text', body, fold };
}

export function createEmptyPhotoCard(): NoticePhotoCard {
  return { id: crypto.randomUUID(), imageId: '' };
}

export function createEmptyLinkedPhotoCard(): NoticeLinkedPhotoCard {
  return { id: crypto.randomUUID(), imageId: '', href: null };
}

export function createNoticePhotosPart(
  cards: NoticePhotoCard[] = [createEmptyPhotoCard()],
): NoticePhotosPart {
  return { id: crypto.randomUUID(), kind: 'photos', cards };
}

export function createNoticeLinkedPhotosPart(
  cards: NoticeLinkedPhotoCard[] = [createEmptyLinkedPhotoCard()],
): NoticeLinkedPhotosPart {
  return { id: crypto.randomUUID(), kind: 'linkedPhotos', cards };
}

export function isPhotoStripPart(
  part: NoticePart,
): part is NoticePhotosPart | NoticeLinkedPhotosPart {
  return part.kind === 'photos' || part.kind === 'linkedPhotos';
}
